import React, { useEffect, useState } from "react"
import DataTable from "react-data-table-component"
import exportFromJSON from "export-from-json"
import { Button } from "react-bootstrap"
import * as XLSX from "xlsx"
import DataTablePagination from "../Pagination/DataTablePagination"
import DataTableBackEndPagination from "../Pagination/DataTableBackEndPagination"

// -------------- Export Component --------------
const Export = ({ data }) => {
  const fileName = "user-data"

  const exportCSV = () => {
    const exportType = exportFromJSON.types.csv
    exportFromJSON({ data, fileName, exportType })
  }

  const exportExcelXLSX = (data, fileName) => {
    const workbook = XLSX.utils.book_new()
    const worksheet = XLSX.utils.json_to_sheet(data)
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1")
    XLSX.writeFile(workbook, `${fileName}.xlsx`, {
      bookType: "xlsx",
      type: "binary",
    })
  }

  const exportExcel = () => {
    exportExcelXLSX(data, fileName)
  }

  return (
    <div className="export-options d-flex align-items-center me-2">
      <div className="export-title small me-2">Exportar</div>
      <div className="btn-group">
        <Button variant="outline-light" onClick={exportCSV}>
          CSV
        </Button>
        <Button variant="outline-light" onClick={exportExcel}>
          Excel
        </Button>
      </div>
    </div>
  )
}

// -------------- Expanded Component (opcional) --------------
const expandedComponent = ({ data }) => {
  return (
    <ul className="data-details p-3 gap gy-1 border-bottom small">
      <li>
        <span className="data-title text-base fw-medium me-2">Name:</span>
        <span className="data-text text-light">{data.name}</span>
      </li>
      <li>
        <span className="data-title text-base fw-medium me-2">Email:</span>
        <span className="data-text text-light">{data.email}</span>
      </li>
    </ul>
  )
}

// -------------- Custom Checkbox (opcional) --------------
const customCheckbox = React.forwardRef(({ onClick, ...rest }, ref) => (
  <div className="form-check" id={rest.name}>
    <input
      type="checkbox"
      className="form-check-input"
      ref={ref}
      onClick={onClick}
      {...rest}
    />
  </div>
))

function DataTableComponent({
  data,
  columns,
  className,
  expandableRows,
  actions,
  tableClassName,
  selectableRows,
  pageCount,
  searchByList = ["name"],
  backEndPagination = false,
  fetchPaginatedData = false,
  currentPaginationPage = false,
  backendFilter = false,
  filterFunc = false,
  isLeadForm = false,
  stateOptions = [],
  customExpandedComponent = false,
  ...props
}) {
  const [tableData, setTableData] = useState(data)
  const [mobileView, setMobileView] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showItemPerPage, setShowItemPerPage] = useState(10)
  const [showItemPerPageBackend, setShowItemPerPageBackend] = useState(10)
  const [itemPerPageBackend, setItemPerPageBackend] = useState(10)

  const [searchBy, setSearchBy] = useState({})
  const [filterBy, setFilterBy] = useState({})

  const handleFilter = async () => {
    setLoading(true)

    if (backendFilter) {
      const searchObject = {}
      Object.keys(searchBy).forEach((key) => {
        const possibleValue = searchBy[key]?.text
        if (possibleValue) {
          searchObject[key] = possibleValue
        }
      })
      await filterFunc(1, searchObject)
      setFilterBy(searchObject)
    } else {
      // Filtrado en el front-end
      let filteredData = data
      Object.keys(searchBy).forEach((key) => {
        const text = searchBy[key]?.text || ""
        if (!text) return
        if (key === "state") {
          // Filtrar por estado: buscamos en la lista de state_assignment
          filteredData = filteredData.filter((item) => {
            if (item.state_assignment && Array.isArray(item.state_assignment)) {
              return item.state_assignment.some((sa) => {
                return (
                  sa.state &&
                  typeof sa.state.state === "string" &&
                  sa.state.state.toLowerCase().includes(text.toLowerCase())
                )
              })
            }
            return false
          })
        } else {
          // Filtros para otros campos (incluyendo fechas, ya que estos ya son tipo date)
          filteredData = filteredData.filter((item) => {
            const value = String(item[key] ?? "").toLowerCase()
            return value.includes(text.toLowerCase())
          })
        }
      })
      setTableData(filteredData)
    }
    setLoading(false)
  }

  const handleClearFilter = async () => {
    setSearchBy({})
    setTableData(data)
    if (backendFilter) {
      setLoading(true)
      await filterFunc(1, {})
      setFilterBy({})
      setLoading(false)
    }
  }

  // Detectar el ancho de la ventana para usar expandableRows en móviles
  const viewChange = () => {
    if (window.innerWidth < 960 && expandableRows) {
      setMobileView(true)
    } else {
      setMobileView(false)
    }
  }

  useEffect(() => {
    window.addEventListener("load", viewChange)
    window.addEventListener("resize", viewChange)
    return () => {
      window.removeEventListener("resize", viewChange)
    }
  }, [])

  useEffect(() => {
    setTableData(data)
  }, [data])

  return (
    <div className="data-table-wrapper">
      <div className="data-table-top" style={{ flexWrap: "wrap" }}>
        {/* Campos de texto para los filtros definidos en searchByList */}
        {searchByList.map((term) => (
          <div key={term} className="data-table-search">
            <input
              className="form-control"
              placeholder={`Buscar por ${term}`}
              type="text"
              value={searchBy[term]?.text || ""}
              onChange={(e) => {
                setSearchBy({
                  ...searchBy,
                  [term]: { text: e.target.value },
                })
              }}
            />
          </div>
        ))}
        {/* Si isLeadForm es true, mostramos también los filtros de fecha y estado */}
        {isLeadForm && (
          <>
            <div className="data-table-search">
              <input
                type="date"
                className="form-control"
                placeholder="Fecha inicio"
                value={searchBy.start_date?.text || ""}
                onChange={(e) => {
                  setSearchBy({
                    ...searchBy,
                    start_date: { text: e.target.value },
                  })
                }}
              />
            </div>
            <div className="data-table-search">
              <input
                type="date"
                className="form-control"
                placeholder="Fecha fin"
                value={searchBy.end_date?.text || ""}
                onChange={(e) => {
                  setSearchBy({
                    ...searchBy,
                    end_date: { text: e.target.value },
                  })
                }}
              />
            </div>
            <div className="data-table-search">
              <input
                type="text"
                className="form-control"
                placeholder="Estado"
                value={searchBy.state?.text || ""}
                onChange={(e) => {
                  setSearchBy({
                    ...searchBy,
                    state: { text: e.target.value },
                  })
                }}
              />
            </div>
          </>
        )}
        <Button variant="outline-primary" onClick={handleFilter}>
          {loading ? <span>Filtrando...</span> : <span>Filtrar</span>}
        </Button>
        <Button variant="outline-primary" onClick={handleClearFilter}>
          <span>Limpiar filtro</span>
        </Button>
        <br />
        {actions && <Export data={data} />}
        {!backEndPagination && (
          <div className="data-table-select">
            <select
              className="form-select"
              onChange={(e) => setShowItemPerPage(e.target.value)}
              value={showItemPerPage}
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="15">15</option>
              <option value="20">20</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
            <span className="text">Registros por página</span>
          </div>
        )}
        {backEndPagination && isLeadForm && (
          <div className="data-table-select">
            <select
              className="form-select"
              onChange={async (e) => {
                setLoading(true)
                setShowItemPerPageBackend(e.target.value)
                setItemPerPageBackend(e.target.value)
                await filterFunc(1, filterBy, e.target.value)
                setLoading(false)
              }}
              value={showItemPerPageBackend}
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="15">15</option>
              <option value="20">20</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
            <span className="text">Leads por página</span>
          </div>
        )}
      </div>
      <DataTable
        data={tableData}
        className={tableClassName}
        noDataComponent={
          <div className="p-2">No se encontraron resultados.</div>
        }
        sortIcon={<div className="data-table-sorter"></div>}
        pagination
        expandableRows={customExpandedComponent ? true : mobileView}
        expandableRowsComponent={customExpandedComponent || expandedComponent}
        selectableRows={selectableRows}
        selectableRowsComponent={customCheckbox}
        columns={columns.map((column) => ({
          ...column,
          style: {
            ...column.style,
            minWidth: column.minWidth || "250px",
          },
        }))}
        paginationComponent={({
          rowsPerPage,
          rowCount,
          onChangePage,
          onChangeRowsPerPage,
          currentPage,
        }) => (
          <div className="data-table-bottom">
            {backEndPagination ? (
              <DataTableBackEndPagination
                showItemPerPage={showItemPerPageBackend}
                itemPerPage={itemPerPageBackend}
                totalItems={pageCount}
                paginate={backendFilter ? filterFunc : fetchPaginatedData}
                currentPage={currentPaginationPage}
                onChangeRowsPerPage={onChangeRowsPerPage}
                setShowItemPerPage={setShowItemPerPage}
                backendFilter={backendFilter}
                filterBy={filterBy}
              />
            ) : (
              <DataTablePagination
                showItemPerPage={showItemPerPage}
                itemPerPage={rowsPerPage}
                totalItems={rowCount}
                paginate={onChangePage}
                currentPage={currentPage}
                onChangeRowsPerPage={onChangeRowsPerPage}
                setShowItemPerPage={setShowItemPerPage}
              />
            )}
          </div>
        )}
      />
    </div>
  )
}

export default DataTableComponent
